/**
 * VendorDataGapsCard — compact dashboard widget that surfaces vendor
 * records missing key fields (email / phone / map pin / location). Each
 * field is a single tile. Tap a tile → opens a sheet with the list of
 * vendors missing that field, each row inline-editable in place.
 *
 * Designed so an operator can clear gaps in 60 seconds without ever
 * leaving the dashboard.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { API, useAuth } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Phone, MapPin, Map, Loader2, Save, ExternalLink, ListChecks, X, Check, UserPlus2, Wand2, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import LocationAutocomplete from '@/components/LocationAutocomplete';

const FIELD_META = {
  phone:    { icon: Phone,  label: 'Contact #', tint: 'text-rose-700 border-rose-200 bg-rose-50' },
  email:    { icon: Mail,   label: 'Email',     tint: 'text-sky-700 border-sky-200 bg-sky-50' },
  pin:      { icon: Map,    label: 'Map pin',   tint: 'text-amber-800 border-amber-200 bg-amber-50' },
  location: { icon: MapPin, label: 'Location',  tint: 'text-violet-700 border-violet-200 bg-violet-50' },
};

const fetcher = async (path, opts = {}) => {
  const t = localStorage.getItem('session_token');
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}), ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

export default function VendorDataGapsCard() {
  const { user } = useAuth();
  const [counts, setCounts] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const [deepLinkTaskId, setDeepLinkTaskId] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const isSuperAdmin = user?.role === 'Super Admin';

  const load = useCallback(async () => {
    try {
      const c = await fetcher('/pollen/data-quality/vendor-gaps');
      setCounts(c);
    } catch { setCounts({ total: 0, email: 0, phone: 0, pin: 0, location: 0 }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep-link support:
  //   /pollen-dots-dashboard?gap=phone          → auto-open the matching tile
  //   /pollen-dots-dashboard?gap=phone&task=ID  → also scope to that data-gap task
  useEffect(() => {
    const usp = new URLSearchParams(window.location.search);
    const g = usp.get('gap');
    const t = usp.get('task');
    if (g && Object.keys(FIELD_META).includes(g)) {
      setActiveField(g);
      setDeepLinkTaskId(t || null);
    }
  }, []);

  const tiles = Object.keys(FIELD_META);

  return (
    <Card data-testid="vendor-data-gaps-card">
      <CardContent className="p-3 sm:p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold flex items-center gap-1.5"><ListChecks className="w-4 h-4 text-amber-700" />Vendor data gaps</h3>
            <p className="text-[11px] text-muted-foreground">Tap a tile to fix missing fields inline.</p>
          </div>
          {counts && <span className="text-[11px] text-muted-foreground">{counts.total} vendors</span>}
        </div>
        {/* One-shot Activities migration — surfaced only to Super Admin so
            the production cleanup can run with a single tap. Safe to
            re-run; backend dedups by (name, location). */}
        {isSuperAdmin && (
          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs h-7 border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
            disabled={migrating}
            onClick={async () => {
              if (!window.confirm('Move all Activity vendor rows into the static catalog and delete them from vendors?')) return;
              setMigrating(true);
              try {
                const t = localStorage.getItem('session_token');
                const res = await fetch(`${API}/pollen/migrate-activities-to-catalog`, {
                  method: 'POST', credentials: 'include',
                  headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
                });
                if (!res.ok) throw new Error(await res.text());
                const j = await res.json();
                toast.success(`Moved ${j.moved}, deleted ${j.deleted_from_vendors}. Catalog: ${j.catalog_total}.`);
                load();
              } catch (e) { toast.error('Migration failed'); }
              finally { setMigrating(false); }
            }}
            data-testid="migrate-activities-btn"
          >
            {migrating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
            Move Activities → static catalog
          </Button>
        )}
        <div className="grid grid-cols-4 gap-2">
          {tiles.map(f => {
            const Icon = FIELD_META[f].icon;
            const c = counts ? (counts[f] || 0) : '…';
            const disabled = counts && counts[f] === 0;
            return (
              <button
                key={f}
                onClick={() => !disabled && setActiveField(f)}
                disabled={!!disabled}
                className={`rounded-lg border p-2 flex flex-col items-center gap-1 transition ${disabled ? 'opacity-40 cursor-not-allowed border-muted' : `${FIELD_META[f].tint} hover:brightness-95`}`}
                data-testid={`gap-tile-${f}`}
                title={`${c} vendor(s) missing ${FIELD_META[f].label}`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-[10px] font-medium">{FIELD_META[f].label}</span>
                <span className="text-lg font-bold leading-none">{c}</span>
              </button>
            );
          })}
        </div>
      </CardContent>

      <GapFillSheet
        open={!!activeField}
        field={activeField}
        taskId={deepLinkTaskId}
        onOpenChange={(v) => { if (!v) { setActiveField(null); setDeepLinkTaskId(null); load(); } }}
      />
    </Card>
  );
}

// ===========================================================================
// Inline-fill sheet — lists vendors missing the picked field. Each row has
// an inline <Input> that saves on Enter / blur. Optional "Assign to" sends
// the whole set as a data-gap task to a team member.
// ===========================================================================

function GapFillSheet({ open, field, onOpenChange, taskId = null }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);

  useEffect(() => {
    if (!open || !field) return;
    setLoading(true);
    const params = new URLSearchParams({ field });
    if (taskId) params.set('task_id', taskId);
    else if (typeFilter) params.set('type', typeFilter);
    fetcher(`/pollen/data-quality/vendor-gaps/list?${params.toString()}`)
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open, field, typeFilter, taskId]);

  if (!field) return null;
  const meta = FIELD_META[field];
  const Icon = meta.icon;

  const onSavedRow = (vendorId) => {
    if (taskId) {
      // In task-scoped mode keep the row visible with its new done state
      // so the operator gets a green-tick confirmation on the very list
      // they were assigned. Refetch to pick up the latest server values.
      const params = new URLSearchParams({ field, task_id: taskId });
      fetcher(`/pollen/data-quality/vendor-gaps/list?${params.toString()}`)
        .then(d => setRows(Array.isArray(d) ? d : []))
        .catch(() => {});
    } else {
      // Default behaviour — drop the row so the org-wide list shrinks.
      setRows(prev => prev.filter(r => r.id !== vendorId));
    }
    toast.success('Saved');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="z-[80] max-h-[92vh] overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+5rem)] lg:pb-6 rounded-t-2xl" data-testid="gap-fill-sheet">
        <SheetHeader>
          <SheetTitle className="text-base flex items-center gap-2"><Icon className="w-4 h-4" />Fill missing {meta.label}</SheetTitle>
          <SheetDescription className="text-[11px]">
            Type the value and press Enter — saves directly to the vendor. No back button needed.
          </SheetDescription>
        </SheetHeader>

        {taskId && (
          <div className="mt-2 px-2 py-1.5 rounded-md border border-amber-300 bg-amber-50 text-[11px] text-amber-900" data-testid="gap-task-scope-banner">
            Scoped to your assigned task — showing only the rows in this task, not the org-wide list.
          </div>
        )}

        {/* Type filter + assign action */}
        <div className="mt-3 flex items-center gap-2">
          <div className="flex-1 max-w-[12rem]">
            <Label className="text-xs">Vendor type</Label>
            <Select value={typeFilter || 'all'} onValueChange={v => setTypeFilter(v === 'all' ? '' : v)} disabled={!!taskId}>
              <SelectTrigger className="h-9 text-sm" data-testid="gap-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="Hotel">Hotels</SelectItem>
                <SelectItem value="Guide">Guides</SelectItem>
                <SelectItem value="Driver">Drivers</SelectItem>
                <SelectItem value="Operator">Operators</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto self-end">
            <Button size="sm" variant="outline" onClick={() => setAssignOpen(true)} disabled={rows.length === 0} data-testid="gap-assign-btn">
              <UserPlus2 className="w-4 h-4 mr-1" />Assign as task ({rows.length})
            </Button>
          </div>
        </div>

        <div className="mt-2 text-xs text-muted-foreground">
          {loading ? 'Loading…' : (
            taskId
              ? `${rows.filter(r => r._done).length} of ${rows.length} done`
              : `${rows.length} vendor(s) need this field`
          )}
        </div>

        <div className="mt-2 space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {loading ? <div className="text-xs text-muted-foreground flex items-center gap-2 p-3"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div> :
           rows.length === 0 ? <p className="text-xs text-muted-foreground p-3">{taskId ? 'No vendors in this task.' : 'All caught up for this field.'}</p> :
           rows.map(v => <GapRow key={v.id} vendor={v} field={field} onSaved={() => onSavedRow(v.id)} />)
          }
        </div>

        <div className="mt-3 flex justify-end">
          <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} data-testid="gap-done-btn"><X className="w-4 h-4 mr-1" />Done</Button>
        </div>

        <AssignGapDialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          field={field}
          typeFilter={typeFilter}
          vendorCount={rows.length}
        />
      </SheetContent>
    </Sheet>
  );
}

function GapRow({ vendor, field, onSaved }) {
  const [val, setVal] = useState('');
  const [saving, setSaving] = useState(false);
  const isDone = !!vendor?._done;

  const placeholder = {
    email: 'name@hotel.com',
    phone: '+91 9999999999',
    location: 'e.g. Kochi, Munnar, Kasol',
    pin: 'Paste full Google Maps URL',
  }[field];

  const save = async () => {
    if (!val.trim()) { toast.error('Enter a value first'); return; }
    setSaving(true);
    try {
      const res = await fetcher(`/pollen/data-quality/vendor/${vendor.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ field, value: val.trim() }),
      });
      // If this fill cleared an assigned data-gap task, surface it so the
      // operator gets the satisfaction of a closed loop.
      if (res && Array.isArray(res.closed_tasks) && res.closed_tasks.length > 0) {
        toast.success(`Task auto-closed — your shortlist is complete.`);
      }
      onSaved();
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  return (
    <div className={`border rounded-lg p-2 flex items-start gap-2 ${isDone ? 'bg-emerald-50 border-emerald-200' : ''}`} data-testid={`gap-row-${vendor.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {isDone && <Check className="w-4 h-4 text-emerald-700" data-testid={`gap-done-${vendor.id}`} />}
          <span className={`text-sm font-medium truncate ${isDone ? 'text-emerald-900 line-through decoration-emerald-300/60' : ''}`}>{vendor.name || '—'}</span>
          <Badge variant="outline" className="text-[10px] h-4">{vendor.type}</Badge>
          {vendor.location && <span className="text-[11px] text-muted-foreground">· {vendor.location}</span>}
        </div>
        {isDone ? (
          <div className="text-[11px] text-emerald-800 mt-1 truncate">
            ✓ {field === 'pin' ? (vendor.google_maps_url || `${vendor.lat}, ${vendor.lng}`) : vendor[field]}
          </div>
        ) : field === 'location' ? (
          <LocationAutocomplete
            value={val}
            onChange={setVal}
            onSelect={(loc) => { setVal(loc.name || ''); }}
            placeholder={placeholder}
            className="h-9 text-sm mt-1"
            testId={`gap-input-${vendor.id}`}
          />
        ) : (
          <Input
            value={val}
            onChange={e => setVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') save(); }}
            placeholder={placeholder}
            className="h-9 text-sm mt-1"
            data-testid={`gap-input-${vendor.id}`}
          />
        )}
      </div>
      <div className="flex flex-col gap-1">
        {!isDone && (
          <Button size="sm" onClick={save} disabled={saving} className="h-8 px-2.5" data-testid={`gap-save-${vendor.id}`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </Button>
        )}
        <a href={`/pollen-dots/vendor/${vendor.id}`} target="_blank" rel="noopener noreferrer" className="h-8 px-2.5 inline-flex items-center justify-center rounded-md border border-input bg-background hover:bg-muted text-foreground" title="Open full vendor profile" data-testid={`gap-open-${vendor.id}`}>
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
}

function AssignGapDialog({ open, onOpenChange, field, typeFilter, vendorCount }) {
  const [members, setMembers] = useState([]);
  const [assignee, setAssignee] = useState('');
  const [note, setNote] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('18:00');
  const [saving, setSaving] = useState(false);
  // After a successful assign, surface the share panel so the supervisor
  // can fire a WhatsApp / Email / Copy-link to the assignee in one tap.
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    if (!open) return;
    setAssignee(''); setNote(''); setDueDate(''); setDueTime('18:00');
    setConfirm(null);
    fetcher('/pollen/team').then(m => setMembers(Array.isArray(m) ? m : [])).catch(() => setMembers([]));
  }, [open]);

  const submit = async () => {
    if (!assignee) { toast.error('Pick a team member'); return; }
    setSaving(true);
    try {
      let dueIso = null;
      if (dueDate) {
        const t = dueTime || '18:00';
        dueIso = new Date(`${dueDate}T${t}`).toISOString();
      }
      const created = await fetcher('/pollen/data-quality/assign', {
        method: 'POST',
        body: JSON.stringify({
          assignee_email: assignee,
          field,
          type: typeFilter || undefined,
          note,
          due_at: dueIso,
        }),
      });
      toast.success('Task assigned');
      const picked = members.find(m => m.email === assignee) || {};
      setConfirm({
        id: created?.id,
        email: assignee,
        name: picked.name || assignee.split('@')[0],
        phone: picked.phone || '',
        due_at: dueIso,
        field,
        count: vendorCount,
      });
    } catch { toast.error('Could not assign task'); }
    finally { setSaving(false); }
  };

  const profileUrl = `${window.location.origin}/pollen-dots/profile?datagaps=open`;
  const taskUrl = confirm?.id
    ? `${window.location.origin}/pollen-dots-dashboard?gap=${field}&task=${encodeURIComponent(confirm.id)}`
    : profileUrl;
  const shareMsg = confirm
    ? `Hi ${confirm.name || ''}, I've assigned you a data-gap task on Pollen Dots — fill missing *${(FIELD_META[field]?.label || field)}* for ${confirm.count} vendor(s)${confirm.due_at ? ` by ${new Date(confirm.due_at).toLocaleString()}` : ''}.\n\nOpen the inline editor: ${taskUrl}`
    : '';
  const waUrl = confirm?.phone
    ? `https://wa.me/${(confirm.phone || '').replace(/\D+/g, '')}?text=${encodeURIComponent(shareMsg)}`
    : `https://wa.me/?text=${encodeURIComponent(shareMsg)}`;
  const mailto = `mailto:${encodeURIComponent(confirm?.email || '')}?subject=${encodeURIComponent('Data-gap task assigned to you · Pollen Dots')}&body=${encodeURIComponent(shareMsg)}`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="z-[90] max-h-[80vh] overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+5rem)] lg:pb-6 rounded-t-2xl" data-testid="gap-assign-sheet">
        <SheetHeader>
          <SheetTitle className="text-base">Assign as task</SheetTitle>
          <SheetDescription className="text-[11px]">
            Send the {vendorCount} pending row(s) for {FIELD_META[field]?.label} as a populator task to a team member.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-3 space-y-3">
          <div>
            <Label className="text-xs">Assignee</Label>
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger className="h-9 text-sm" data-testid="gap-assignee"><SelectValue placeholder="Pick a team member" /></SelectTrigger>
              <SelectContent>
                {members.map(m => <SelectItem key={m.id || m.email} value={m.email}>{m.name} · {m.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Due date</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="h-9 text-sm" data-testid="gap-due-date" />
            </div>
            <div>
              <Label className="text-xs">Time</Label>
              <Input type="time" value={dueTime} onChange={e => setDueTime(e.target.value)} className="h-9 text-sm" data-testid="gap-due-time" />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground -mt-1">Drives TAT in Pulse — leave blank if there's no hard deadline.</p>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Input value={note} onChange={e => setNote(e.target.value)} className="h-9 text-sm" placeholder="Context for the assignee…" data-testid="gap-note" />
          </div>
        </div>
        {!confirm ? (
          <div className="mt-3 flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={saving || !assignee} data-testid="gap-assign-submit">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Assign
            </Button>
          </div>
        ) : (
          <div className="mt-3 p-3 rounded-md border border-emerald-300 bg-emerald-50 text-xs text-emerald-900 space-y-2" data-testid="gap-assigned-share">
            <div>✓ Task assigned to <strong>{confirm.name}</strong>. Share it so they pick it up.</div>
            <div className="flex flex-wrap gap-2">
              <a href={waUrl} target="_blank" rel="noopener noreferrer"
                 className="h-8 px-2.5 inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-white hover:bg-emerald-100 text-emerald-900 text-xs font-medium"
                 data-testid="gap-share-wa">
                <Megaphone className="w-3.5 h-3.5" /> WhatsApp
              </a>
              <a href={mailto}
                 className="h-8 px-2.5 inline-flex items-center gap-1 rounded-md border border-sky-300 bg-white hover:bg-sky-100 text-sky-900 text-xs font-medium"
                 data-testid="gap-share-email">
                <Mail className="w-3.5 h-3.5" /> Email
              </a>
              <button type="button"
                onClick={() => { navigator.clipboard.writeText(taskUrl); toast.success('Link copied'); }}
                className="h-8 px-2.5 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white hover:bg-amber-100 text-amber-900 text-xs font-medium"
                data-testid="gap-share-copy">
                <ExternalLink className="w-3.5 h-3.5" /> Copy link
              </button>
            </div>
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} data-testid="gap-share-done">Done</Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
